/**
 * Order number in, one order out.
 *
 * Shared by the reprint and address sections. It uses the Orders search route, which
 * already projects the row for the role, so whatever comes back is safe to show here.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components';
import { ApiProblem } from '@/lib/api';
import { benchApi, normaliseOrderNumber, pickOrder } from './api';
import type { BenchOrderRow } from './types';

export interface OrderFinderProps {
  /** Distinguishes the two inputs on the page for labels and ids. */
  idPrefix: string;
  onFound: (order: BenchOrderRow) => void;
  onCleared?: () => void;
}

export function OrderFinder({ idPrefix, onFound, onCleared }: OrderFinderProps) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function find(event: FormEvent): Promise<void> {
    event.preventDefault();
    const number = normaliseOrderNumber(value);
    if (!number) return;
    setBusy(true);
    setError(null);
    try {
      const page = await benchApi.findOrders(number);
      const order = pickOrder(page.items, number);
      if (!order) {
        setError(`No order ${number} found. Check the number on the slip.`);
        onCleared?.();
      } else {
        onFound(order);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiProblem
          ? caught.message
          : 'The lookup did not run. Check the connection and try again.',
      );
      onCleared?.();
    } finally {
      setBusy(false);
    }
  }

  const inputId = `${idPrefix}-order`;

  return (
    <form className="bench-finder" onSubmit={(event) => void find(event)}>
      <div className="o-field bench-finder__field">
        <label className="o-field__label" htmlFor={inputId}>
          Order number
        </label>
        <input
          id={inputId}
          className="o-input o-input--mono"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder="12345"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
        />
        {error ? (
          <span className="o-field__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>
      <Button type="submit" variant="primary" busy={busy} disabled={normaliseOrderNumber(value) === ''}>
        Find order
      </Button>
    </form>
  );
}

/** The one line that says which order is on the bench. */
export function OrderSummary({ order }: { order: BenchOrderRow }) {
  const where = [order.ship_city, order.ship_state].filter(Boolean).join(', ');
  return (
    <p className="bench-order" data-testid="bench-order-summary">
      <span className="o-mono">#{order.order_number}</span> {order.customer_name}
      {where ? <span className="o-muted">, {where}</span> : null}
      {order.tracking_number ? (
        <span className="o-muted">, label already printed</span>
      ) : null}
    </p>
  );
}
